import React from "react";
import styled from "styled-components";
import {
  FiArrowUpRight,
  FiBookOpen,
  FiBriefcase,
  FiCode,
  FiGlobe,
  FiMonitor,
  FiTool,
  FiUsers,
  FiMapPin,
  FiRefreshCw,
} from "react-icons/fi";
import Card from "./Card";

const highlights = [
  {
    Icon: FiGlobe,
    title: "Web scraping",
    desc: "I build scrapers that turn information from websites into structured, useful data. I enjoy working out how a site works and making data collection easier to repeat.",
    detail: "Python · Selenium · Data extraction",
  },
  {
    Icon: FiMonitor,
    title: "Frontends for businesses",
    desc: "I create frontend apps for small and medium-sized businesses, translating their needs into clear, responsive interfaces that help customers and teams get things done.",
    detail: "Business apps · Responsive UI · Usability",
  },
  {
    Icon: FiCode,
    title: "Apps & full-stack development",
    desc: "I bring app and web app ideas to life, connecting the interface, backend logic, and database. I use CI/CD pipelines to automate builds, tests, and deployments, bringing the complete application from development to delivery.",
    detail: "Frontend · Backend · Databases · CI/CD",
  },
  {
    Icon: FiUsers,
    title: "Project management",
    desc: "I turn ideas into manageable steps, clarify requirements, and keep the work moving. I enjoy coordinating people and technical decisions from the first plan through to delivery.",
    detail: "Planning · Communication · Delivery",
  },
  {
    Icon: FiRefreshCw,
    title: "Automation & botting",
    desc: "I create scripts and bots that take care of repetitive work, from browser workflows to Android game tasks. For me, automation is a practical way to save time and explore what software can do.",
    detail: "Scripts · Bots · Workflow automation",
  },
  {
    Icon: FiTool,
    title: "Solving problems",
    desc: "I like getting to the bottom of a problem, whether it’s a stubborn bug, a slow process, or an idea that needs a technical approach. I break it down, try solutions, and refine what works.",
    detail: "Analysis · Debugging · Experimentation",
  },
];

const AboutMe = () => (
  <Container id="aboutme" aria-labelledby="about-heading">
    <Content>
      <Eyebrow>About me</Eyebrow>
      <Introduction>
        <Story>
          <h2 id="about-heading">
            Curious by nature.
            <br />
            <span>A developer at heart.</span>
          </h2>
          <p>
            I’m Maximilian, a software engineer based in Vienna. I’ve been
            coding since 2015, building web applications, exploring new
            technologies, and finding practical ways to turn ideas into working
            software.
          </p>
          <p>
            I enjoy working across the full stack and understanding how each
            part fits together. My background in development, technical
            analysis, and project coordination helps me approach a problem from
            both the technical and the human side.
          </p>
          <p>
            In my free time, I build apps and web apps, create frontend
            solutions for small and medium-sized businesses, and experiment with
            web scraping, automation, and bots. I enjoy solving practical
            problems and taking an idea from planning to a working application.
            I also share my knowledge through tutoring in programming,
            databases, and mathematics.
          </p>
          <JourneyLink href="#timeline">
            Explore my journey <FiArrowUpRight aria-hidden="true" />
          </JourneyLink>
        </Story>
        <ProfileDetails aria-label="Professional background">
          <Detail>
            <FiBriefcase aria-hidden="true" />
            <div>
              <h3>Where I work</h3>
              <p>
                Full-Stack Developer<span>Erste Digital · Team BON</span>
              </p>
            </div>
          </Detail>
          <Detail>
            <FiBookOpen aria-hidden="true" />
            <div>
              <h3>My foundation</h3>
              <p>
                BSc, Computer Science
                <span>TU Wien · Software and Information Engineering</span>
              </p>
            </div>
          </Detail>
          <Detail>
            <FiMapPin aria-hidden="true" />
            <div>
              <h3>Where I’m based</h3>
              <p>
                Vienna, Austria<span>German, native · English, fluent</span>
              </p>
            </div>
          </Detail>
        </ProfileDetails>
      </Introduction>
      <WorkHeading>
        <span>What I enjoy working on</span>
        <span aria-hidden="true">From an idea to a working solution</span>
      </WorkHeading>
      <Cards>
        {highlights.map((highlight, index) => (
          <Card key={highlight.title} {...highlight} number={`0${index + 1}`} />
        ))}
      </Cards>
    </Content>
  </Container>
);

export default AboutMe;

const Container = styled.section`
  padding: 4rem 1.25rem;
  color: #f2f7f4;
  background:
    radial-gradient(ellipse at 100% 0%, #244e40 0%, transparent 55%), #142f27;

  @media (min-width: 840px) {
    padding: 6.5rem 5%;
  }
`;

const Content = styled.div`
  max-width: 1120px;
  margin-inline: auto;
`;

const Eyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  color: #9bc8b3;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;

  &::before {
    content: "";
    width: 1.75rem;
    height: 1px;
    background: currentColor;
  }
`;

const Introduction = styled.div`
  display: grid;
  align-items: start;
  gap: 2.5rem;

  @media (min-width: 960px) {
    grid-template-columns: minmax(0, 1.45fr) minmax(0, 1fr);
    gap: 5rem;
  }
`;

const Story = styled.div`
  h2 {
    margin-bottom: 1.5rem;
    font-size: clamp(2rem, 4vw, 3.25rem);
    font-weight: 500;
    letter-spacing: -0.055em;
    line-height: 1.15;

    span {
      color: #a6d9bc;
    }
  }

  p {
    max-width: 57ch;
    margin-top: 1rem;
    color: #c1d2c9;
    font-size: 0.9rem;
    line-height: 1.85;
  }
`;

const JourneyLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 44px;
  margin-top: 1.25rem;
  color: #d1f3df;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  border-radius: 4px;

  svg {
    transition: transform 180ms ease;
  }
  &:hover svg {
    transform: translate(2px, -2px);
  }
  &:hover {
    text-decoration: underline;
    text-underline-offset: 5px;
  }
  &:focus-visible {
    outline: 2px solid #a6d9bc;
    outline-offset: 5px;
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
    &:hover svg {
      transform: none;
    }
  }
`;

const ProfileDetails = styled.aside`
  padding: 0.35rem 1.25rem;
  border: 1px solid rgba(190, 224, 204, 0.18);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.035);

  @media (min-width: 600px) {
    padding-inline: 1.75rem;
  }
`;

const Detail = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding-block: 1.5rem;

  & + & {
    border-top: 1px solid rgba(190, 224, 204, 0.13);
  }

  > svg {
    flex-shrink: 0;
    margin-top: 0.15rem;
    color: #a6d9bc;
    font-size: 1.15rem;
  }

  h3 {
    margin-bottom: 0.5rem;
    color: #a6c2b3;
    font-size: 0.65rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  p {
    font-size: 0.95rem;
    font-weight: 500;
    line-height: 1.5;

    span {
      display: block;
      margin-top: 0.35rem;
      color: #b4c9bd;
      font-size: 0.75rem;
      font-weight: 400;
      line-height: 1.7;
    }
  }
`;

const WorkHeading = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(190, 224, 204, 0.18);
  font-size: 0.75rem;
  line-height: 1.6;

  span + span {
    color: #a6c2b3;
    font-size: 0.7rem;
  }

  @media (min-width: 840px) {
    margin-top: 4rem;
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  margin-top: 1.5rem;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 840px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;
